/** Geodesic distance and formatting for the planner — pure, tested.
 * Haversine on the WGS84 mean radius: honest to well under 0.5% at trail
 * scale, and the same method the phone's planner uses for leg lengths. */

import type { ElevationSample, LonLat } from "./types";

const R = 6371008.8; // WGS84 mean radius, meters

export function haversineM(a: LonLat, b: LonLat): number {
  const φ1 = (a[1] * Math.PI) / 180;
  const φ2 = (b[1] * Math.PI) / 180;
  const dφ = φ2 - φ1;
  const dλ = ((b[0] - a[0]) * Math.PI) / 180;
  const s =
    Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

export function lineDistanceM(line: LonLat[]): number {
  let total = 0;
  for (let i = 1; i < line.length; i++) total += haversineM(line[i - 1], line[i]);
  return total;
}

/** Cumulative distance table: cum[i] = meters from line[0] to line[i].
 * cum[0] = 0; length matches the line. */
export function cumulativeM(line: LonLat[]): number[] {
  const cum = new Array<number>(line.length);
  let total = 0;
  for (let i = 0; i < line.length; i++) {
    if (i > 0) total += haversineM(line[i - 1], line[i]);
    cum[i] = total;
  }
  return cum;
}

/** Evenly spaced points along the line for elevation sampling: every
 * `stepM` meters plus every vertex, each tagged with its cumulative
 * distance. Vertices are kept so profile grades break at real corners. */
export function densify(
  line: LonLat[],
  stepM: number,
): { at: LonLat; distM: number }[] {
  if (line.length === 0) return [];
  const out: { at: LonLat; distM: number }[] = [{ at: line[0], distM: 0 }];
  let cum = 0;
  for (let i = 1; i < line.length; i++) {
    const seg = haversineM(line[i - 1], line[i]);
    let d = stepM;
    while (d < seg) {
      const t = d / seg;
      out.push({
        at: [
          line[i - 1][0] + (line[i][0] - line[i - 1][0]) * t,
          line[i - 1][1] + (line[i][1] - line[i - 1][1]) * t,
        ],
        distM: cum + d,
      });
      d += stepM;
    }
    cum += seg;
    out.push({ at: line[i], distM: cum });
  }
  return out;
}

/** Raw positive/negative delta sums, NO smoothing — exact parity with the
 * phone (TripStore.elevationGainMeters / TerrainSampler's profile loop,
 * which are deliberately un-denoised). Web and phone must report the same
 * gain for the same route; a deadband here would make the numbers drift. */
export function gainLossM(samples: ElevationSample[]): {
  gainM: number;
  lossM: number;
} {
  let gain = 0;
  let loss = 0;
  for (let i = 1; i < samples.length; i++) {
    const d = samples[i].eleM - samples[i - 1].eleM;
    if (d > 0) gain += d;
    else loss += -d;
  }
  return { gainM: gain, lossM: loss };
}

/** Max grade over ~100 m windows (span grown to ≤130 m, counted once
 * ≥80 m) so one noisy DEM pixel can't scream 40% — the exact window rule
 * from RouteProfile.build on the phone. Percent. */
export function maxGradePct(samples: ElevationSample[]): number {
  let maxGrade = 0;
  let j = 0;
  for (let i = 0; i < samples.length; i++) {
    while (samples[i].distM - samples[j].distM > 130) j++;
    const span = samples[i].distM - samples[j].distM;
    if (span >= 80) {
      maxGrade = Math.max(
        maxGrade,
        (Math.abs(samples[i].eleM - samples[j].eleM) / span) * 100,
      );
    }
  }
  return maxGrade;
}

/** Trail-speed ETA — the phone's model verbatim: 6.26 m/s flat (14 mph),
 * slowed by grade, floored at 1.8 m/s (~4 mph). */
export function etaSeconds(samples: ElevationSample[]): number {
  const baseMS = 6.26;
  let t = 0;
  for (let i = 1; i < samples.length; i++) {
    const dist = samples[i].distM - samples[i - 1].distM;
    if (dist <= 0) continue;
    const grade = Math.abs(samples[i].eleM - samples[i - 1].eleM) / dist;
    t += dist / Math.max(1.8, baseMS / (1 + 9 * grade));
  }
  return t;
}

// ---------------------------------------------------------- formatting

const FT_PER_M = 3.28084;
const MI_PER_M = 1 / 1609.344;

/** "0.3 mi" / "3.2 mi" / "128 mi" — one decimal under 100, whole above. */
export function formatMiles(m: number): string {
  const mi = m * MI_PER_M;
  if (mi < 100) return `${mi.toFixed(1)} mi`;
  return `${Math.round(mi)} mi`;
}

/** "+480 ft" (rounded to 10 ft) — gain formatting for stats rows. */
export function formatGainFt(m: number): string {
  return `+${(Math.round((m * FT_PER_M) / 10) * 10).toLocaleString()} ft`;
}

export function formatFeet(m: number): string {
  return `${Math.round(m * FT_PER_M).toLocaleString()} ft`;
}

/** "~1:40" — hours:minutes, for the ETA pill. */
export function formatDuration(seconds: number): string {
  const mins = Math.round(seconds / 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `~${h}:${String(m).padStart(2, "0")}`;
}
